import React, { useEffect, useState } from 'react';
import Calendar from '../../sharedComponents/calendar/Calendar';
import { Button, Typography, Box, FormControlLabel, Checkbox, Grid } from '@mui/material';
import { makeStyles, useTheme } from '@mui/styles';
import EventCreationDialog from './EventCreationDialog';
import Moment from 'moment';
import { EventOutlined } from '@mui/icons-material';
import { eventCalendarService, userService } from '../../services';
import EventList from './EventList';
import SnackbarMsg from '../Snackbar/Snackbar';
import Loader from '../Loader/Loader';
import { Role } from '../../helper';
import SingleSelectionDropdown from '../dropdown/SingleSelectionDropdown';
import useTelemetry from '../../utils/useTelemetry';

const useStyles = makeStyles((theme) => ({
    container: {
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        paddingBottom: 0,
        marginTop: 0,
        marginBottom: 0,
        width: '100%',
        overflow: 'auto',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        [theme.breakpoints.down('sm')]: {
            paddingLeft: theme.spacing(0),
            paddingRight: theme.spacing(0),
            marginTop: 0,
        },
    },
    eventBox: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        flexWrap: 'wrap',
        gap: '8px',
    },
    eventCircle: {
        width: 15,
        height: 15,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#333',
    },
    addButtonContainer: {
        display: 'flex',
        justifyContent: 'flex-end', // Aligns content (button) to the right
        width: '100%',
        marginTop: 10,
    },
    addButton: {
        color: "#ffffff",
        borderColor: theme.palette.primary.main,
        height: 33,
        fontSize: { xs: '0.75rem', sm: '0.85rem' },
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.primary.main,
    },
    filterContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        width: '100%',
        marginTop: 0,
        marginBottom: theme.spacing(1),
    },
}));

const CalenderEventComponent = (props) => {
    const theme = useTheme();
    const classes = useStyles();
    const date = new Date();
    const [currentMonth, setCurrentMonth] = useState(date.getMonth());
    const [currentYear, setCurrentYear] = useState(date.getFullYear());
    const [selectedCalendarCell, setSelectedCalendarCell] = useState(date.getTime());
    const [selectedDate, setSelectedDate] = useState(date);
    const [openModal, setOpenModal] = useState(false); // State to control modal open/close
    const [filteredEvents, setFilteredEvents] = useState([]); // State to store events
    const [events, setEvents] = useState([]); // State to store events
    const [loading, setLoading] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState({});
    const [isEdit, setIsEdit] = useState(false);
    const [snackbarValue, setSnackbarValue] = useState({
        openSnackbar: false,
        severity: 'success',
        message: '',
    });
    const [dayWiseEvents, setDayWiseEvents] = useState([]);
    const [eventTypes, setEventTypes] = useState([]);

    const [eventFilters, setEventFilters] = useState('All');
    const userRole = userService.getUserRole();
    const { trackCustomEvents } = useTelemetry();


    useEffect(() => {
        let date = new Date();
        if (currentMonth === date.getMonth() && currentYear === date.getFullYear()) {
            setCalendarCurrentDate();
        } else {
            setSelectedCalendarCell(null);
        }
    }, [currentMonth, currentYear]);

    useEffect(() => {
        const eventsOfDays = getDayWiseEvents(selectedDate);
        if (eventsOfDays) {
            const types = getEventTypesFromData(eventsOfDays);
            const typeWiseEvents = filterEventTypeWise(types, eventsOfDays);
            setDayWiseEvents(typeWiseEvents);
        } else {
            setDayWiseEvents([]);
        }
    }, [selectedDate, filteredEvents]);

    const getCurrentMonth = async (month) => {
        setCurrentMonth(month);
        try {
            let date = new Date();
            await fetchEvents((date.getMonth() + 1), date.getFullYear());
        } catch (err) {
        }
    };

    const getCurrentYear = (year) => {
        setCurrentYear(year);
    };

    const setCalendarCurrentDate = () => {
        let date = new Date();
        let newDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        setSelectedCalendarCell(newDate.getTime());
        setSelectedDate(newDate); // Update selected date
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                let date = new Date();
                // Use Promise.all to wait for both requests to complete
                await Promise.all([fetchEvents((date.getMonth() + 1), date.getFullYear()), fetchEventTypes()]);
            } catch (err) {
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        if (eventFilters && eventFilters !== "All") {
            onEventTypeChange(eventFilters);
        }
    }, [events]);

    const fetchEvents = async (month, year) => {
        try {
            setLoading(true)
            const params = `${month}/${year}`;
            const eventsRes = await eventCalendarService.readEventsAsync(params);

            if (eventsRes) {
                setEvents(eventsRes);
                setFilteredEvents(eventsRes);
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
        }
    }

    const fetchEventTypes = async () => {
        const eventTypeResponse = await eventCalendarService.readEventTypesAsync();
        if (eventTypeResponse) {
            setEventTypes(eventTypeResponse)
        }
    }

    const handleAddEvent = () => {
        setIsEdit(false);
        setSelectedEvent({})
        setOpenModal(true); // Open the modal when "Add Event" is clicked
    };

    const handleCloseModal = () => {
        setOpenModal(false); // Close the modal
        setIsEdit(false);
        setSelectedEvent({})
    };

    const handleDateChange = (date) => {
        if (date) {
            setSelectedDate(date);
            setSelectedCalendarCell(date.getTime());
        }
        trackCustomEvents("ClickEvent", { event: "Day Event Viewed" });
    };

    const onMonthChange = async (month, year) => {
        await fetchEvents((month + 1), year);
        let date = new Date();
        if (month === date.getMonth() && year === date.getFullYear()) {
            setSelectedDate(date);
            setSelectedCalendarCell(date.getTime());
        } else {
            const monthStartDate = new Date(year, month, 1);
            setSelectedDate(monthStartDate);
            setSelectedCalendarCell(monthStartDate.getTime());
        }
    }

    const handleOnDelete = async (id) => {
        setLoading(true);

        const res = await eventCalendarService.deleteEventsAsync(id);

        if (res) {
            await fetchEvents((currentMonth + 1), currentYear);
            handleSnackbar(true, `Event Deleted Successfully!`);
        } else {
            setLoading(false);
            handleSnackbar(true, 'Something went wrong.', 'error');
        }
    }

    const handleOnEdit = (event) => {
        setSelectedEvent(event);
        setIsEdit(true);
        setOpenModal(true);

    }

    const onSuccess = async () => {
        handleCloseModal();
        const date = selectedDate ? new Date(selectedDate) : new Date();
        const month = date.getMonth() + 1;
        const year = date.getFullYear();

        await fetchEvents(month, year);
        handleSnackbar(true, `Event ${isEdit ? "Updated" : "Published"} Successfully!`);
    }

    const handleSnackbar = (open, message, severity) => {
        setSnackbarValue(prevState => ({
            ...prevState,
            openSnackbar: open,
            message: message,
            severity: severity,
        }));
        setTimeout(() => {
            setSnackbarValue(prevState => ({
                ...prevState,
                openSnackbar: false
            }));
        }, 2000);
    };

    const getDayWiseEvents = (date) => {
        if (date) {
            const day = Moment(Moment(date).format('MM/DD/YYYY'));
            const list = filteredEvents?.filter(item => {
                if (item.StartDate && item.EndDate) {
                    const startDay = Moment(item.StartDate);
                    const endDay = Moment(item.EndDate);
                    return (startDay.isSameOrBefore(day) && endDay.isSameOrAfter(day));
                } else {
                    const startDay = Moment(item.StartDate);
                    return (startDay.isSame(day));
                }
            });
            return list;
        }
        return [];
    }

    const getEventTypesFromData = (events) => {
        let types = [];
        events?.forEach(item => {
            if (!types.includes(item.Type)) {
                types.push(item.Type);
            }
        })
        return types;
    }

    const filterEventTypeWise = (types, dayWiseEvents) => {
        let typeWiseEvent = [];
        types?.forEach(type => {
            const typeEvents = dayWiseEvents?.filter(event => event.Type === type);
            if (typeEvents) {
                typeWiseEvent = [...typeWiseEvent, ...typeEvents];
            }
        })
        return typeWiseEvent;
    }

    const getEventTypes = () => {
        let types = [{
            Text: 'All',
            Id: 'All'
        }, ...eventTypes || []];
        return types;
    }

    const onEventTypeChange = (type) => {
        setEventFilters(type);
        if (type == 'All') {
            setFilteredEvents(events);
        } else {
            const eventsList = filterEventTypeWise([type], events);
            setFilteredEvents(eventsList);
        }
    }

    const cellRenderer = (date) => {
        if (filteredEvents && filteredEvents.length) {
            try {
                const eventList = getDayWiseEvents(date);
                const eventsObject = {}
                eventList?.forEach(element => {
                    if (eventsObject[element.Type]) {
                        eventsObject[element.Type]['count'] = eventsObject[element.Type]['count'] + 1;
                    } else {
                        eventsObject[element.Type] = {
                            'count': 1,
                            'color': element.Color
                        }
                    }
                });

                if (eventsObject && Object.keys(eventsObject).length) {
                    return (
                        <div style={{ flexDirection: 'row', flex: 1, display: 'flex', flexWrap: 'wrap' }}>
                            {Object.keys(eventsObject).map(key => {
                                const { count, color } = eventsObject[key];
                                return <Box sx={{
                                    width: {
                                        xs: '16px',  // 13px width for extra-small screens
                                        sm: '20px',   // 20px width for small screens
                                    },
                                    height: {
                                        xs: '16px',  // 13px height for extra-small screens
                                        sm: '20px',   // 20px height for small screens
                                    }
                                }}
                                    style={{
                                        marginRight: 2,
                                        backgroundColor: color ? color : "#ffffff",
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#333'
                                    }}>
                                    <Typography variant="subtitle1" sx={{
                                        fontSize: { xs: '0.70rem', sm: '0.85rem' },
                                        lineHeight: { xs: '1.60', sm: '1.5' }
                                    }}>
                                        {count}
                                    </Typography>
                                </Box>
                            })
                            }
                        </div>
                    );
                } else
                    return null;
            } catch (error) {
                return null;
            }
        } else
            return null
    };

    return (
        <Box className={classes.container}>
            <Box className={classes.filterContainer}>
                <SingleSelectionDropdown
                    title={'Filter Event By Type'}
                    value={eventFilters}
                    dropdownData={getEventTypes()}
                    handleValueChange={(event) => onEventTypeChange(event.target.value)}
                    keys={{ id: 'Id', value: 'Id', text: 'Text', color: 'Color' }}
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}
                    viewSx={{
                        height: {
                            xs: '28px',  // 13px height for extra-small screens
                            sm: '33px',   // 20px height for small screens
                        },
                        width: '100%',
                        fontSize: { xs: '0.75rem', sm: '0.85rem' },
                    }}
                    showLegend={true}
                />
            </Box>

            <Calendar
                getCurrentMonth={(month) => getCurrentMonth(month)}
                getCurrentYear={(year) => getCurrentYear(year)}
                selectedCell={selectedCalendarCell}
                onCellClick={(date) => { handleDateChange(date); }}
                onMonthChange={(month, year) => onMonthChange(month, year)}
                cellRenderer={cellRenderer}
            />
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginTop: 0,
            }}>
                {/* <Grid container
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        flexWrap: 'wrap',
                    }}
                >
                    {eventTypes.map((legend, index) => (
                        <Box key={index} sx={{display: 'flex', alignItems: 'center'}}>
                            <Box
                                sx={{
                                    width: {
                                        xs: '13px',  // 13px width for extra-small screens
                                        sm: '15px',   // 20px width for small screens
                                    },
                                    height: {
                                        xs: '13px',  // 13px height for extra-small screens
                                        sm: '15px',   // 20px height for small screens
                                    },
                                    backgroundColor: legend.Color,
                                    marginRight: 5,  // Space between color box and label
                                }}
                            />
                            <Typography style={{ marginRight: 10 }} sx={{ fontSize: { xs: '0.70rem', sm: '0.85rem' } }} variant="body2">{legend.Text}</Typography>
                        </Box>
                    ))}
                </Grid> */}

                {(userRole === Role.Admin) ? <Box className={classes.addButtonContainer}>
                    <Button
                        variant="contained"
                        startIcon={<EventOutlined />}
                        onClick={handleAddEvent}
                        className={classes.addButton}
                    >
                        Add Event
                    </Button>
                </Box> : null}
            </Box>

            {/* Event List */}
            {dayWiseEvents && dayWiseEvents.length ?
                <EventList
                    events={dayWiseEvents}
                    selectedDate={selectedDate}
                    onDelete={handleOnDelete}
                    onEdit={handleOnEdit}
                    eventTypes={eventTypes}
                /> : null}

            <EventCreationDialog
                openModal={openModal}
                handleCloseModal={() => setOpenModal(false)}
                isEdit={isEdit}
                payload={isEdit ? selectedEvent : {}}
                selectedDate={selectedDate ? Moment(new Date(selectedDate)).format('YYYY-MM-DD') : new Date()}
                onSuccess={onSuccess}
                eventTypes={eventTypes}
            />

            <SnackbarMsg
                showError={snackbarValue.openSnackbar}
                onClose={() => {
                    setSnackbarValue(prevState => ({
                        ...prevState,
                        openSnackbar: false
                    }));
                }}
                severity={snackbarValue.severity}
                msg={snackbarValue.message}
            />
            <Loader isLoading={loading} />
        </Box>
    );
};

export default CalenderEventComponent;
